<!-- Copyright (c) 2020-2022 ForgeRock. All rights reserved.

This software may be modified and distributed under the terms
of the MIT license. See the LICENSE file for details. -->
<template>
  <BDropdown
    :right="right"
    variant="link"
    toggle-class="text-decoration-none p-0">
    <slot name="tenant-header" />
    <template #button-content>
      <!-- @slot Button content -->
      <slot name="button-content" />
    </template>
    <!-- @slot Dropdown header -->
    <slot name="dropdown-header" />
    <template v-for="(item, index) in dropdownItems">
      <template>
        <FrMenuItem
          :key="`sideDropdownItems_${index}`"
          v-bind="item"
          :user-roles="userDetails.roles" />
      </template>
    </template>
    <template v-if="showProfileLink">
      <BDropdownDivider v-if="enableLogout && dropdownItems.length" />
      <BDropdownItem
        class="mb-2"
        @click="$router.push({ name: 'Profile' })">
        <FrIcon  class="mr-2" name="account_circle" />
        My Profile
      </BDropdownItem>
      <BDropdownDivider v-if="enableLogout" />
    </template>
    <template>
      <BDropdownItem class="mb-2"  href="https://whitepages.noaa.gov/"  target="_blank"  rel="noopener noreferrer">
        <FrIcon class="mr-2"  name="open_in_browser" />
        {{ $t('common.whitePages') }}
      </BDropdownItem>
      <BDropdownDivider />
    </template>
    <BDropdownItem
      v-if="enableLogout"
      class="mb-2"
      @click="logoutUser()">
      <FrIcon
        class="mr-2"
        name="exit_to_app"
      />
      {{ $t('common.signOut') }}
    </BDropdownItem>
  </BDropdown>
</template>

<script>
import {
  BDropdown,
  BDropdownItem,
  BDropdownDivider,
  BMedia,
} from 'bootstrap-vue';
import MenuItem from '@forgerock/platform-shared/src/components/MenuItem';
import LoginMixin from '@forgerock/platform-shared/src/mixins/LoginMixin';
import FrIcon from '@forgerock/platform-shared/src/components/Icon';

/**
 * Bootstrap dropdown menu used in navbar and sidemenu
 */
export default {
  name: 'DropdownMenu',
  components: {
    BDropdown,
    BDropdownDivider,
    BDropdownItem,
    BMedia,
    FrMenuItem: MenuItem,
    FrIcon,
  },
  mixins: [
    LoginMixin,
  ],
  props: {
    /**
     * Items contained in dropdown.
     */
    dropdownItems: {
      type: Array,
      default: () => [],
    },
    /**
     * Enable logout link in dropdown.
     */
    enableLogout: {
      type: Boolean,
      default: true,
    },
    /**
     * Link to profile.
     */
    showProfileLink: {
      type: Boolean,
      default: true,
    },
    /**
     * Align dropdown menu with right edge of dropdown button.
     */
    right: {
      type: Boolean,
      default: false,
    },
    /**
     * Details about the current user. Displayed with admin and profile links.
     */
    userDetails: {
      type: Object,
      default: () => ({
        name: 'Fake Name',
        company: '',
        email: 'email@fake.com',
        adminURL: 'wwwfakecom',
        roles: [],
      }),
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .b-dropdown {
    width: 100%;
  }

  .dropdown-menu.show {
    position: absolute;
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.175);
    padding: 0;
    min-width: 243px;
    max-width: 270px;

    li:first-child {
      margin-top: 0.5rem;
    }

    li:last-child {
      margin-bottom: 0.5rem;
    }
  }

  .dropdown-toggle {
    color: $fr-sidemenu-font-color;
    white-space: nowrap;
    overflow: visible;
    text-overflow: clip;
  }

  .dropdown-item {
    h5,
    span:not(.material-icons-outlined) {
      font-size: 0.875rem;
    }
  }

  .dropdown-toggle::after {
    right: 0.6875rem;
  }

  @include media-breakpoint-down(md) {
    .dropdown-toggle::after {
      right: 0;
    }
  }
}

.media-body {
  overflow: visible;
  white-space: nowrap;
}

::v-deep .fr-dropdown-button-content {
  white-space: nowrap !important;
  overflow: visible !important;
  text-overflow: clip !important;
  max-width: none !important;
}
</style>
